import { useEffect } from 'react';

import { getAccountsQueryConfig } from '@api/lendingAPI/queryConfigs';
import { getCountry } from '@iwIntlProvider/CountryProvider';
import { tracking } from '@iwoca/frontend-tracking-library';
import {
    fetchGetApplication,
    fetchGetFormalOffers
} from '@iwoca/lapi-client/edge';
import { RootState } from '@iwProvider/configureStore';
import { useQuery } from '@tanstack/react-query';
import { connect } from 'react-redux';

import ApprovalExpiredView from './approvalExpiredView/ApprovalExpiredView';
import MainContentPending from '../pageStructure/mainContentPending';
import AccessUserApprovedView from './approvedView/accessUserApprovedView/AccessUserApprovedView';
import { ApprovedView as OldApprovedView } from './approvedView/ApprovedView';
import DeclinedView from './declinedView/DeclinedView';
import DefaultView from './defaultView/DefaultView';
import DeferredView from './deferredView/DeferredView';
import { getFormalOfferIds } from './getFormalOfferIds';
import { getOfferStatus, TOfferStatus } from './getOfferStatus';
import { ApprovedView } from './offersPage/ApprovedView/ApprovedView';
import { isNewOffersPage } from './offersPage/offersPage.helpers';
import { ApplicationPendingView } from './pendingView/applicationPendingView/ApplicationPendingView';
import RevokedView from './revokedView/RevokedView';
import { useStateKey } from '../../iwQueryClient/useStateKey';
import { useTrackPageView } from '../../tracking/pageView';
import FallbackErrorModal from '../modals/fallbackErrorModal/FallbackErrorModal';

const mapStateToProps = (state: RootState) => ({
    isAccessUser: state.siteContext.isAccessUser
});

type PropsFromRedux = ReturnType<typeof mapStateToProps>;

const MyOffers = ({ isAccessUser }: PropsFromRedux) => {
    useTrackPageView({ name: 'My Offers' });

    const stateKey = useStateKey();

    const {
        isLoading: getFormalOffersResponseLoading,
        isError: getFormalOffersResponseError,
        data: getFormalOffersResponse
    } = useQuery({
        queryKey: ['formal_offers', stateKey],
        queryFn: () => fetchGetFormalOffers({ stateKey })
    });

    const {
        isLoading: getApplicationResponseLoading,
        isError: getApplicationResponseError,
        data: getApplicationResponse
    } = useQuery({
        queryKey: ['application', stateKey],
        queryFn: () => fetchGetApplication({ stateKey })
    });

    const {
        isLoading: getAccountsResponseLoading,
        isError: getAccountsResponseError,
        data: getAccountsResponse
    } = useQuery({
        ...getAccountsQueryConfig(stateKey)
    });

    const loading =
        getApplicationResponseLoading ||
        getFormalOffersResponseLoading ||
        getAccountsResponseLoading;
    const error =
        getApplicationResponseError ||
        getFormalOffersResponseError ||
        getAccountsResponseError;

    let offerStatus: TOfferStatus | null = null;

    useEffect(() => {
        //amplitude tracking
        if (
            getFormalOffersResponse &&
            getApplicationResponse &&
            getApplicationResponse.data &&
            getFormalOffersResponse.data &&
            offerStatus
        ) {
            const formalOfferIds = getFormalOfferIds(getFormalOffersResponse);

            tracking.ampli.myOffersVisited({
                formal_offer_ids: formalOfferIds,
                application_status: getApplicationResponse!.data!.status,
                page_status: offerStatus
            });
        }
    }, [getApplicationResponse, getFormalOffersResponse, offerStatus]);

    if (loading) {
        return <MainContentPending data-testid="my-offers-loading" />;
    }
    if (error) {
        return <FallbackErrorModal />;
    }
    if (isAccessUser) {
        return <AccessUserApprovedView />;
    }

    offerStatus = getOfferStatus(
        getFormalOffersResponse!,
        getApplicationResponse!,
        getAccountsResponse!
    );

    switch (offerStatus) {
        case 'application_declined':
            return <DeclinedView />;
        case 'application_pending':
            return <ApplicationPendingView />;
        case 'application_deferred':
            return <DeferredView />;
        case 'offer_expired':
            return <ApprovalExpiredView />;
        case 'application_offer_revoked':
            return getCountry() === 'DE' ? <RevokedView /> : <DefaultView />;
        case 'offered':
            return isNewOffersPage() ? <ApprovedView /> : <OldApprovedView />;
        case 'no_offer':
            return <DefaultView />;
        default:
            return <DefaultView />;
    }
};

// eslint-disable-next-line import/no-default-export
export default connect(mapStateToProps)(MyOffers);
